import {
  GoalConfigurationData,
  GoalCorrectionType,
  GoalDailySummary,
  GoalDayItem,
  GoalDefinitionInput,
  GoalDefinitionOption,
  GoalPeriodItem,
  GoalPeriodTemplateItem,
  GoalPeriodTemplateType,
  GoalSavedTemplate,
  GoalSavedTemplateDetails,
  GoalSavedTemplateItemInput,
  GoalTemplateConflictStrategy,
  GoalTemplateExchangeRow,
  GoalTemplateImportResult,
  GoalSourceOptionsData,
  GoalTemplateData,
  GoalWeeklyModelItemInput
} from '../models/goal.model';

export abstract class GoalsRepository {
  abstract loadDay(targetDate: string, refresh?: boolean): Promise<GoalDayItem[]>;
  abstract loadWeek(weekStart: string, refresh?: boolean): Promise<GoalDayItem[]>;
  abstract loadExistingWeek(weekStart: string): Promise<GoalDayItem[]>;
  abstract loadDailySummary(targetDate: string, refresh?: boolean): Promise<GoalDailySummary>;
  abstract loadPeriods(referenceDate: string, refresh?: boolean): Promise<GoalPeriodItem[]>;
  abstract loadPeriodTemplate(periodType: GoalPeriodTemplateType): Promise<GoalPeriodTemplateItem[]>;
  abstract saveDayTemplateFromDate(targetDate: string): Promise<number>;
  abstract applyDayTemplate(targetDate: string): Promise<number>;
  abstract loadSavedTemplates(): Promise<GoalSavedTemplate[]>;
  abstract loadSavedTemplateDetails(templateId: number): Promise<GoalSavedTemplateDetails>;
  abstract updateSavedTemplate(templateId: number, name: string, items: GoalSavedTemplateItemInput[], dayTemplateIds: Array<number | null>): Promise<void>;
  abstract exportSavedTemplates(templateIds: number[] | null): Promise<GoalTemplateExchangeRow[]>;
  abstract importSavedTemplates(rows: GoalTemplateExchangeRow[], conflictStrategy: GoalTemplateConflictStrategy): Promise<GoalTemplateImportResult>;
  abstract saveNamedDayTemplate(name: string, targetDate: string): Promise<number>;
  abstract applyNamedDayTemplate(templateId: number, targetDate: string): Promise<number>;
  abstract saveNamedWeekTemplateDirect(name: string, weekStart: string): Promise<number>;
  abstract saveNamedWeekTemplateFromDays(name: string, dayTemplateIds: Array<number | null>): Promise<number>;
  abstract applyNamedWeekTemplate(templateId: number, weekStart: string): Promise<number>;
  abstract saveNamedPeriodTemplate(name: string, periodType: GoalPeriodTemplateType, referenceDate: string): Promise<number>;
  abstract applyNamedPeriodTemplate(templateId: number, referenceDate: string): Promise<number>;
  abstract loadDefinitions(): Promise<GoalDefinitionOption[]>;
  abstract loadSourceOptions(): Promise<GoalSourceOptionsData>;
  abstract loadTemplateData(): Promise<GoalTemplateData>;
  abstract loadConfiguration(): Promise<GoalConfigurationData>;

  abstract createDefinition(input: GoalDefinitionInput): Promise<number>;
  abstract updateDefinition(definitionId: number, input: GoalDefinitionInput): Promise<void>;
  abstract archiveDefinition(definitionId: number): Promise<void>;
  abstract restoreDefinition(definitionId: number): Promise<void>;
  abstract replaceWeeklyModel(items: GoalWeeklyModelItemInput[]): Promise<void>;
  abstract applyWeeklyModel(weekStart: string): Promise<number>;
  abstract replacePeriodTemplateFromPeriod(periodType: GoalPeriodTemplateType, referenceDate: string): Promise<number>;
  abstract applyPeriodTemplate(periodType: GoalPeriodTemplateType, referenceDate: string): Promise<number>;

  abstract recalculateDate(targetDate: string): Promise<void>;
  abstract setManualValue(measurementId: number, value: number | null): Promise<void>;
  abstract setCorrection(
    measurementId: number,
    type: GoalCorrectionType,
    value: number
  ): Promise<void>;
  abstract clearCorrection(measurementId: number): Promise<void>;
  abstract setPeriodManualValue(targetId: number, value: number | null): Promise<void>;
  abstract setPeriodCorrection(targetId: number, type: GoalCorrectionType, value: number): Promise<void>;
  abstract clearPeriodCorrection(targetId: number): Promise<void>;
  abstract deactivatePeriodTarget(targetId: number): Promise<void>;
  abstract upsertPeriodTarget(
    goalDefinitionId: number,
    referenceDate: string,
    targetValue: number | null
  ): Promise<void>;

  abstract upsertDailyException(
    goalDefinitionId: number,
    targetDate: string,
    targetValue: number,
    position?: number | null
  ): Promise<void>;
  abstract deactivateDailyTarget(targetId: number): Promise<void>;
}
